import type { AuthSession, LegalConsentsPayload } from "@mvk/shared";
import { isApiEnabled } from "../api/config";
import { visitorAuthApi } from "../api/auth";
import { visitorAuthStorage } from "../api/authStorage";
import { ApiError } from "../api/client";
import { maxPlatform } from "./maxPlatform";
import { debugLog } from "./debugLog";
import { mapAuthUserToProfile, visitorSessionService } from "./visitorSessionService";
import { getPendingQr } from "./publicQrFlow";
import { isMaxMiniAppHost } from "./channelHosts";
import type { User } from "../types";

export type MaxContactPayload = {
  phone: string;
  authDate?: string;
  hash?: string;
};

/** After explicit logout, skip silent MAX re-login until user starts auth again. */
const SKIP_MAX_BOOTSTRAP_KEY = "mvk.max.skip_bootstrap";

function shouldSkipMaxBootstrap(): boolean {
  try {
    return sessionStorage.getItem(SKIP_MAX_BOOTSTRAP_KEY) === "1";
  } catch {
    return false;
  }
}

function setSkipMaxBootstrap(skip: boolean) {
  try {
    if (skip) sessionStorage.setItem(SKIP_MAX_BOOTSTRAP_KEY, "1");
    else sessionStorage.removeItem(SKIP_MAX_BOOTSTRAP_KEY);
  } catch {
    /* ignore */
  }
}

/** Session cache — avoid a second MAX contact dialog after consents (IDEA-029). */
let cachedMaxContact: MaxContactPayload | null = null;

export type NeedBadgeVerifyError = Error & {
  code: "need_badge_verify";
  reason?: string;
  contact: MaxContactPayload;
  phone: string;
};

export function isNeedBadgeVerifyError(err: unknown): err is NeedBadgeVerifyError {
  return (
    err instanceof Error &&
    (err as NeedBadgeVerifyError).code === "need_badge_verify" &&
    Boolean((err as NeedBadgeVerifyError).contact)
  );
}

export const visitorAuthService = {
  isLoggedIn(): boolean {
    return visitorAuthStorage.isAuthenticated();
  },

  /** Inside MAX WebApp SDK or dedicated MAX mini-app host. */
  isMaxRuntime(): boolean {
    return maxPlatform.isAvailable() || isMaxMiniAppHost();
  },

  /** True MAX WebApp with initData (can share phone). */
  isMaxWebApp(): boolean {
    return maxPlatform.isAvailable() && Boolean(maxPlatform.getInitData());
  },

  async sendOtp(phone: string) {
    return visitorAuthApi.sendOtp(phone);
  },

  async verifyOtp(phone: string, code: string): Promise<AuthSession> {
    return visitorAuthApi.verifyOtp(phone, code, getPendingQr() ?? undefined);
  },

  async loginWithMax(initData?: string, claimCode?: string): Promise<AuthSession> {
    const payload = initData?.trim() || maxPlatform.getInitData();
    if (!payload) throw new Error("max_init_data_missing");
    setSkipMaxBootstrap(false);
    return visitorAuthApi.maxInit(
      payload,
      claimCode ?? getPendingQr() ?? undefined,
    );
  },

  async loginWithMaxContact(legalConsents?: LegalConsentsPayload): Promise<AuthSession> {
    const initData = maxPlatform.getInitData();
    if (!initData) throw new Error("max_init_data_missing");
    setSkipMaxBootstrap(false);
    debugLog.info("auth", "requestContact start", {
      platform: maxPlatform.getPlatform(),
      initDataLen: initData.length,
      userId: maxPlatform.getUser()?.id,
      cached: Boolean(cachedMaxContact),
    });
    const contact =
      cachedMaxContact ??
      (await maxPlatform.requestContact().then((c) => {
        cachedMaxContact = {
          phone: c.phone,
          authDate: c.authDate || undefined,
          hash: c.hash || undefined,
        };
        return cachedMaxContact;
      }));
    debugLog.info("auth", "requestContact ok", {
      phoneTail: contact.phone.slice(-4),
      authDate: contact.authDate || null,
      hashLen: contact.hash?.length ?? 0,
      verified: Boolean(contact.authDate && contact.hash),
    });
    try {
      return await visitorAuthApi.maxContact({
        initData,
        phone: contact.phone,
        authDate: contact.authDate || undefined,
        hash: contact.hash || undefined,
        legalConsents,
      });
    } catch (err) {
      if (err instanceof ApiError && err.code === "need_badge_verify") {
        const body = (err.body ?? {}) as { reason?: string; message?: string; phone?: string };
        const need: NeedBadgeVerifyError = Object.assign(
          new Error(body.message || "need_badge_verify"),
          {
            code: "need_badge_verify" as const,
            reason: body.reason,
            phone: body.phone || contact.phone,
            contact: {
              phone: contact.phone,
              authDate: contact.authDate,
              hash: contact.hash,
            },
          },
        );
        throw need;
      }
      throw err;
    }
  },

  /**
   * Badge login: MAX (verified contact) or PWA (typed phone only).
   */
  async loginWithBadge(
    code: string,
    opts?: {
      contact?: MaxContactPayload;
      phone?: string;
      legalConsents?: LegalConsentsPayload;
    },
  ): Promise<AuthSession> {
    const initData = maxPlatform.getInitData() || undefined;
    const contact = opts?.contact;
    setSkipMaxBootstrap(false);
    return visitorAuthApi.badgeLogin({
      code,
      initData,
      phone: contact?.phone ?? opts?.phone,
      authDate: contact?.authDate,
      hash: contact?.hash,
      legalConsents: opts?.legalConsents,
    });
  },

  canSharePhoneInMax(): boolean {
    return maxPlatform.canRequestContact();
  },

  /** Session-cached MAX contact after requestContact (no second dialog). */
  getCachedMaxContact(): MaxContactPayload | null {
    return cachedMaxContact;
  },

  async tryMaxBootstrap(
    updateUser: (partial: Partial<User>) => void,
    claimCode?: string,
  ): Promise<boolean> {
    if (!isApiEnabled() || this.isLoggedIn() || !maxPlatform.isAvailable()) {
      return false;
    }
    // Explicit logout backdoor: do not silently re-auth via initData.
    if (shouldSkipMaxBootstrap()) {
      debugLog.info("auth", "tryMaxBootstrap skipped (logout backdoor)");
      return false;
    }

    const initData = maxPlatform.getInitData();
    if (!initData) return false;

    try {
      const fromStart = maxPlatform.resolveQrOpaqueFromStartParam();
      const session = await this.loginWithMax(initData, claimCode ?? fromStart ?? undefined);
      if (typeof updateUser === "function") {
        updateUser(mapAuthUserToProfile(session.user));
        await visitorSessionService.syncProfile(updateUser);
      }
      return true;
    } catch {
      return false;
    }
  },

  logout() {
    cachedMaxContact = null;
    setSkipMaxBootstrap(true);
    visitorAuthApi.logout();
  },

  shouldRequireLogin(): boolean {
    return isApiEnabled() && !visitorAuthStorage.isAuthenticated();
  },
};

export const VISITOR_DEMO_PHONE = "+7 (926) 123-45-67";
